import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../services/api';
import { Spinner, showToast } from '../components/UI';

/* ─────────────────────────────────────────────
   Shared helpers
───────────────────────────────────────────── */
const ACCEPTED = '.pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv,.zip';

function formatRelative(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function initials(userId = '') {
  return userId.slice(0, 2).toUpperCase();
}

function mimeIcon(mimeType = '') {
  if (mimeType.includes('pdf'))   return '📄';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  if (mimeType.startsWith('image')) return '🖼️';
  return '📎';
}

function humanSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}

/* ─────────────────────────────────────────────
   apiFetchVoid — same as apiFetch but skips
   res.json() for endpoints that return 204.
───────────────────────────────────────────── */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3070';

async function apiFetchVoid(path, options = {}) {
  const { auth: firebaseAuth } = await import('../services/firebase');
  const token = firebaseAuth.currentUser
    ? await firebaseAuth.currentUser.getIdToken()
    : null;
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
}

/* ─────────────────────────────────────────────
   API wrappers — all calls use apiFetch so the
   Firebase auth token is attached automatically.
───────────────────────────────────────────── */
const feedbackApi = {
  list: (entityType, entityId) => {
    const url = `/api/feedback?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`;
    console.log('[feedbackApi.list] fetching:', url);
    return apiFetch(url);
  },

  create: (payload) =>
    apiFetch('/api/feedback', { method: 'POST', body: JSON.stringify(payload) }),

  update: (id, payload) =>
    apiFetch(`/api/feedback/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),

  delete: (id) =>
    apiFetchVoid(`/api/feedback/${id}`, { method: 'DELETE' }),   // 204 No Content

  getUploadUrl: (filename, mimeType, entityType, entityId) =>
    apiFetch('/api/feedback/upload-url', {
      method: 'POST',
      body: JSON.stringify({ filename, mime_type: mimeType, entity_type: entityType, entity_id: entityId }),
    }),
};

/* ─────────────────────────────────────────────
   Upload a single file: get signed URL → PUT directly to GCS
───────────────────────────────────────────── */
async function uploadFile(file, entityType, entityId, onProgress) {
  const { upload_url, public_url } = await feedbackApi.getUploadUrl(
    file.name, file.type || 'application/octet-stream', entityType, entityId
  );

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', upload_url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload  = () => (xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`)));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });

  return {
    name:       file.name,
    url:        public_url,
    mime_type:  file.type || 'application/octet-stream',
    size_bytes: file.size,
  };
}

/* ─────────────────────────────────────────────
   AttachmentChip
───────────────────────────────────────────── */
function AttachmentChip({ att, onRemove }) {
  return (
    <a
      href={onRemove ? undefined : att.url}
      target={onRemove ? undefined : '_blank'}
      rel="noreferrer"
      title={att.name}
      style={{
        display:        'inline-flex',
        alignItems:     'center',
        gap:            '5px',
        padding:        '4px 9px 4px 7px',
        borderRadius:   '999px',
        background:     'var(--paper-2, #f0ece4)',
        border:         '1px solid var(--paper-3, #e0d8cc)',
        fontSize:       '11px',
        color:          'var(--ink-soft, #555)',
        textDecoration: 'none',
        cursor:         onRemove ? 'default' : 'pointer',
        maxWidth:       '180px',
        flexShrink:     0,
        whiteSpace:     'nowrap',
      }}
    >
      <span>{mimeIcon(att.mime_type)}</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100px' }}>{att.name}</span>
      {att.size_bytes && <span style={{ color: 'var(--ink-mute, #999)', fontSize: '10px' }}>{humanSize(att.size_bytes)}</span>}
      {onRemove && (
        <button
          onClick={onRemove}
          style={{
            background: 'none', border: 'none', padding: '0 0 0 2px',
            cursor: 'pointer', color: 'var(--ink-mute, #999)', fontSize: '13px', lineHeight: 1,
          }}
        >×</button>
      )}
    </a>
  );
}

/* ─────────────────────────────────────────────
   UploadProgress bar
───────────────────────────────────────────── */
function UploadProgress({ files }) {
  if (!files.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '6px' }}>
      {files.map((f, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--ink-mute, #999)' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>{f.name}</span>
            <span>{f.progress}%</span>
          </div>
          <div style={{ height: '3px', borderRadius: '2px', background: 'var(--paper-3, #e0d8cc)' }}>
            <div style={{ height: '100%', borderRadius: '2px', width: `${f.progress}%`, background: 'var(--teal, #5dcaa5)', transition: 'width 0.2s' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   FeedbackForm  (add or edit)
───────────────────────────────────────────── */
function FeedbackForm({ entityType, entityId, initial, onSave, onCancel }) {
  const isEdit = !!initial;
  const [text,       setText]       = useState(initial?.text || '');
  const [stagedFiles, setStagedFiles] = useState([]);          // File objects not yet uploaded
  const [uploadProgress, setUploadProgress] = useState([]);   // [{name, progress}]
  const [savedAtts,  setSavedAtts]  = useState(initial?.attachments || []);  // already-uploaded
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const addFiles = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setStagedFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const removeStagedFile = (idx) => setStagedFiles(prev => prev.filter((_, i) => i !== idx));
  const removeExistingAtt = (idx) => setSavedAtts(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!text.trim() && stagedFiles.length === 0 && savedAtts.length === 0) {
      showToast('Add text or attach a file');
      return;
    }
    setSubmitting(true);

    // Upload staged files concurrently, tracking per-file progress
    const progressMap = stagedFiles.map(f => ({ name: f.name, progress: 0 }));
    setUploadProgress(progressMap);

    let newAtts = [];
    try {
      newAtts = await Promise.all(
        stagedFiles.map((file, idx) =>
          uploadFile(file, entityType, entityId, (pct) => {
            setUploadProgress(prev => prev.map((p, i) => i === idx ? { ...p, progress: pct } : p));
          })
        )
      );
    } catch (err) {
      showToast(err.message || 'Upload failed');
      setSubmitting(false);
      setUploadProgress([]);
      return;
    }

    const allAtts = [...savedAtts, ...newAtts];

    try {
      if (isEdit) {
        await onSave({ text: text.trim() || null, attachments: allAtts });
      } else {
        await onSave({ entity_type: entityType, entity_id: entityId, text: text.trim() || null, attachments: allAtts });
      }
      // Reset form after successful create (not edit, which unmounts via parent)
      if (!isEdit) {
        setText('');
        setStagedFiles([]);
        setSavedAtts([]);
      }
    } catch (err) {
      showToast(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
      setUploadProgress([]);
    }
  };

  return (
    <div
      style={{
        display:       'flex',
        flexDirection: 'column',
        gap:           '8px',
        padding:       '12px',
        borderRadius:  '10px',
        background:    'var(--white, #fff)',
        border:        '1px solid var(--paper-3, #e0d8cc)',
        boxShadow:     '0 2px 8px rgba(0,0,0,0.04)',
      }}
    >
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Share feedback, notes, or observations…"
        rows={6}
        style={{
          width:       '100%',
          resize:      'vertical',
          border:      '1px solid var(--paper-3, #e0d8cc)',
          borderRadius:'8px',
          padding:     '8px 10px',
          fontSize:    '13px',
          lineHeight:  1.6,
          fontFamily:  'var(--sans, sans-serif)',
          color:       'var(--ink, #1a1a1a)',
          background:  'var(--paper, #faf8f4)',
          outline:     'none',
          boxSizing:   'border-box',
        }}
        onFocus={e => { e.target.style.borderColor = 'var(--teal, #5dcaa5)'; }}
        onBlur={e  => { e.target.style.borderColor = 'var(--paper-3, #e0d8cc)'; }}
      />

      {/* Existing attachments (edit mode) */}
      {savedAtts.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
          {savedAtts.map((a, i) => (
            <AttachmentChip key={a.url || `saved-${i}`} att={a} onRemove={() => removeExistingAtt(i)} />
          ))}
        </div>
      )}

      {/* Staged (not yet uploaded) */}
      {stagedFiles.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
          {stagedFiles.map((f, i) => (
            <AttachmentChip
              key={`staged-${f.name}-${i}`}
              att={{ name: f.name, url: '#', mime_type: f.type, size_bytes: f.size }}
              onRemove={() => removeStagedFile(i)}
            />
          ))}
        </div>
      )}

      <UploadProgress files={uploadProgress} />

      {/* Footer row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={submitting}
          style={{
            display:    'inline-flex',
            alignItems: 'center',
            gap:        '5px',
            padding:    '6px 10px',
            borderRadius:'8px',
            border:     '1px dashed var(--paper-3, #e0d8cc)',
            background: 'none',
            fontSize:   '12px',
            color:      'var(--ink-soft, #666)',
            cursor:     'pointer',
          }}
        >
          📎 Attach files
        </button>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPTED}
          multiple
          onChange={addFiles}
          style={{ display: 'none' }}
        />

        <div style={{ display: 'flex', gap: '6px' }}>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              style={ghostBtnStyle}
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              display:    'inline-flex',
              alignItems: 'center',
              gap:        '6px',
              padding:    '6px 14px',
              borderRadius:'8px',
              border:     'none',
              background: submitting ? 'var(--paper-3, #e0d8cc)' : 'var(--ink, #1a1a1a)',
              color:      submitting ? 'var(--ink-mute, #999)' : 'var(--white, #fff)',
              fontSize:   '12px',
              fontWeight: '600',
              cursor:     submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting && <Spinner size={12} />}
            {isEdit ? 'Save' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   FeedbackEntry  (single item in the list)
───────────────────────────────────────────── */
function FeedbackEntry({ entry, currentUserId, entityType, entityId, onUpdated, onDeleted }) {
  const [editing,  setEditing]  = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirm,  setConfirm]  = useState(false);

  const isOwner = String(entry.user_id) === String(currentUserId);

  const handleSaveEdit = async (payload) => {
    const updated = await feedbackApi.update(entry.id, payload);
    onUpdated(updated);
    setEditing(false);
    showToast('Feedback updated');
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await feedbackApi.delete(entry.id);
      onDeleted(entry.id);
      showToast('Feedback deleted');
    } catch (err) {
      showToast(err.message || 'Delete failed');
      setDeleting(false);
      setConfirm(false);
    }
  };

  return (
    <div
      style={{
        display:       'flex',
        flexDirection: 'column',
        gap:           '8px',
        padding:       '12px',
        borderRadius:  '10px',
        background:    'var(--white, #fff)',
        border:        '1px solid var(--paper-3, #e0d8cc)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <div
            style={{
              width:       '26px',
              height:      '26px',
              borderRadius:'50%',
              background:  'var(--paper-3, #e0d8cc)',
              display:     'flex',
              alignItems:  'center',
              justifyContent:'center',
              fontSize:    '10px',
              fontWeight:  '700',
              color:       'var(--ink-soft, #666)',
              flexShrink:  0,
            }}
          >
            {initials(String(entry.user_id))}
          </div>
          <span style={{ fontSize: '11px', color: 'var(--ink-mute, #999)' }}>{formatRelative(entry.created_at)}</span>
          {entry.updated_at !== entry.created_at && (
            <span style={{ fontSize: '10px', color: 'var(--ink-mute, #999)', fontStyle: 'italic' }}>(edited)</span>
          )}
        </div>

        {isOwner && !editing && (
          <div style={{ display: 'flex', gap: '4px' }}>
            <button onClick={() => setEditing(true)} style={iconBtnStyle} title="Edit">✏️</button>
            {confirm ? (
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', color: 'var(--ink-mute, #999)' }}>Delete?</span>
                <button onClick={handleDelete} disabled={deleting} style={{ ...iconBtnStyle, color: '#c8563a' }}>
                  {deleting ? <Spinner size={10} /> : '✓'}
                </button>
                <button onClick={() => setConfirm(false)} style={iconBtnStyle}>✕</button>
              </div>
            ) : (
              <button onClick={() => setConfirm(true)} style={iconBtnStyle} title="Delete">🗑️</button>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      {editing ? (
        <FeedbackForm
          entityType={entityType}
          entityId={entityId}
          initial={entry}
          onSave={handleSaveEdit}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <>
          {entry.text && (
            <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.65, color: 'var(--ink, #1a1a1a)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {entry.text}
            </p>
          )}
          {entry.attachments?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {entry.attachments.map((a, i) => <AttachmentChip key={a.url || `att-${i}`} att={a} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   FeedbackPanel  (the public export)

   Props
   -----
   entityType   string   e.g. 'artifact'
   entityId     string   UUID of the entity
   currentUserId string  logged-in user UUID (for owner checks)
   isMobile     bool
───────────────────────────────────────────── */
export default function FeedbackPanel({ entityType, entityId, currentUserId, isMobile }) {
  const [entries,  setEntries]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    console.log('[FeedbackPanel] load() called, entityId:', entityId, 'entityType:', entityType);
    if (!entityId) {
      console.warn('[FeedbackPanel] entityId is falsy, aborting');
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      console.log('[FeedbackPanel] calling feedbackApi.list...');
      const data = await feedbackApi.list(entityType, entityId);
      console.log('[FeedbackPanel] got data:', data);
      setEntries(data);
    } catch (err) {
      console.error('[FeedbackPanel] load failed:', err);
      setLoadError(err.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const handleCreated = async (payload) => {
    try {
      const entry = await feedbackApi.create(payload);
      setEntries(prev => [entry, ...prev]);
      showToast('Feedback submitted');
    } catch (err) {
      console.error('[FeedbackPanel] create failed:', err);
      showToast(err.message || 'Failed to submit feedback');
    }
  };

  const handleUpdated = (updated) => {
    setEntries(prev => prev.map(e => e.id === updated.id ? updated : e));
  };

  const handleDeleted = (id) => {
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  const panelStyle = {
    display:       'flex',
    flexDirection: 'column',
    gap:           '12px',
    // Mobile: floating card below artifact; Desktop: top of sidebar
    ...(isMobile ? {
      margin:       '16px 0 8px',
      padding:      '16px 14px',
      borderRadius: '12px',
      background:   'rgba(255,255,255,0.7)',
      border:       '1px solid var(--paper-3, #e0d8cc)',
      backdropFilter:'blur(10px)',
    } : {}),
  };

  return (
    <div style={panelStyle}>
      {/* Section header */}
      <div
        style={{
          display:       'flex',
          alignItems:    'center',
          justifyContent:'space-between',
          paddingBottom: '8px',
          borderBottom:  '1px solid var(--paper-3, #e0d8cc)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-mute, #999)' }}>
            Need changes. Send Feedback<br/>
          </span>
          {entries.length > 0 && (
            <span
              style={{
                fontSize:    '10px',
                fontWeight:  '600',
                padding:     '1px 6px',
                borderRadius:'999px',
                background:  'var(--paper-3, #e0d8cc)',
                color:       'var(--ink-soft, #666)',
              }}
            >
              {entries.length}
            </span>
          )}
        </div>
      </div>

      {/* Compose form — always visible */}
      <FeedbackForm
        entityType={entityType}
        entityId={entityId}
        onSave={handleCreated}
      />

      {/* Feed */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
          <Spinner size={18} />
        </div>
      ) : loadError ? (
        <p style={{ fontSize: '12px', color: '#c8563a', textAlign: 'center', margin: '8px 0 4px' }}>
          {loadError}
        </p>
      ) : entries.length === 0 ? (
        <p style={{ fontSize: '12px', color: 'var(--ink-mute, #999)', textAlign: 'center', margin: '8px 0 4px', lineHeight: 1.5 }}>
          No feedback yet.<br />Be the first to add a note.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {entries.map(entry => (
            <FeedbackEntry
              key={entry.id}
              entry={entry}
              currentUserId={currentUserId}
              entityType={entityType}
              entityId={entityId}
              onUpdated={handleUpdated}
              onDeleted={handleDeleted}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Shared micro-styles ── */
const ghostBtnStyle = {
  background: 'none',
  border:     '1px solid var(--paper-3, #e0d8cc)',
  cursor:     'pointer',
  fontSize:   '12px',
  color:      'var(--ink-soft, #666)',
  padding:    '5px 10px',
  borderRadius:'8px',
};

const iconBtnStyle = {
  background: 'none',
  border:     'none',
  cursor:     'pointer',
  fontSize:   '13px',
  padding:    '2px 4px',
  borderRadius:'4px',
  lineHeight: 1,
};